import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CafeProvider } from './context/CafeContext';
import { LanguageProvider } from './context/LanguageContext';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import GlobalNotification from './components/GlobalNotification';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import SystemDashboard from './pages/SystemDashboard';
import PublicBill from './pages/PublicBill';
import Orders from './pages/Orders';
import Counter from './pages/Counter';
import Dues from './pages/Dues';
import Savings from './pages/Savings';
import Expenses from './pages/Expenses';
import Reports from './pages/Reports';
import Staff from './pages/Staff';
import Settings from './pages/Settings';
import UserManagement from './pages/UserManagement';
import Contact from './pages/Contact';

/**
 * A super admin does not run a till - they run the businesses that do. So `/`
 * gives them the system console, and the cafe dashboard stays reachable at
 * /dashboard for when they scope into a particular tenant. Everyone else sees
 * the till dashboard at both paths, which keeps existing links working.
 *
 * A sales agent lands on the same console, cut down to the businesses they
 * registered - it is the only page they have, and ProtectedRoute sends them
 * back here from anywhere else.
 */
function Home() {
  const { user } = useAuth();
  const runsTheSystem = user?.role === 'super_admin' || user?.role === 'sales';
  return runsTheSystem ? <SystemDashboard /> : <Dashboard />;
}

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <CafeProvider>
          <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <GlobalNotification />
            <Routes>
              {/* Public Routes */}
              <Route path="/login" element={<Login />} />
              {/* Opened by a customer from WhatsApp or a QR scan. Outside
                  ProtectedRoute and outside Layout on purpose: they have no login,
                  and the app chrome means nothing to them. */}
              <Route path="/bill/:shareToken" element={<PublicBill />} />

              {/* Protected Routes */}
              <Route path="/" element={
                <ProtectedRoute>
                  <Layout>
                    <Home />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <Layout>
                    <Dashboard />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/orders" element={
                <ProtectedRoute>
                  <Layout>
                    <Orders />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/counter" element={
                <ProtectedRoute>
                  <Layout>
                    <Counter />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/dues" element={
                <ProtectedRoute>
                  <Layout>
                    <Dues />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/savings" element={
                <ProtectedRoute>
                  <Layout>
                    <Savings />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/expenses" element={
                <ProtectedRoute>
                  <Layout>
                    <Expenses />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/reports" element={
                <ProtectedRoute>
                  <Layout>
                    <Reports />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/staff" element={
                <ProtectedRoute>
                  <Layout>
                    <Staff />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/settings" element={
                <ProtectedRoute>
                  <Layout>
                    <Settings />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/users" element={
                <ProtectedRoute>
                  <Layout>
                    <UserManagement />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/contact" element={
                <ProtectedRoute>
                  <Layout>
                    <Contact />
                  </Layout>
                </ProtectedRoute>
              } />
            </Routes>
          </Router>
        </CafeProvider>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;